import React from "react";
import parse from "html-react-parser";
import { LazyLoadImage } from "react-lazy-load-image-component";

import "../styles/app.scss";
import "react-lazy-load-image-component/src/effects/blur.css";
import { SectionAnimation } from "./SectionAnimation";
import { TopAttribution } from "./Attribution";
import { MapSequence } from "./Map";
import { BarChart, ColumnChart, WaffleChart } from "./Charts";
import { OutboundLink } from "./OutboundLink";
import { NewsletterSignupBanner } from "./NewsletterSignup";

const convertToHtml = (text: string) => {
  let formattedText = text;

  // Make links outbound:
  formattedText = formattedText.replace(
    "<a href=",
    '<a target="_blank" rel="noopener noreferrer" href=',
  );

  // Make italics (photo credits) have space above:
  formattedText = formattedText.replace(
    "<em>",
    '<em style="display: inline-block; margin-top: 1rem;">',
  );

  // Fix double spaces and non-spaced commas:
  formattedText = formattedText.replace("  ", " ").replace(",", ", ");

  return parse(formattedText);
};

export const Paragraph: React.FC<{ text: string }> = ({ text }) => (
  <p className="copy">{convertToHtml(text)}</p>
);

export const splitParagraphs = (content: string) =>
  content.split("{newParagraph}");

export const formatContent = (content: string) => (
  <>
    {splitParagraphs(content).map((paragraph, i) => (
      <Paragraph key={i} text={paragraph} />
    ))}
  </>
);

export const TextBlock = ({
  children,
  customStyle,
}: {
  children: React.ReactNode;
  customStyle?: React.CSSProperties;
}) => (
  <div className="hero">
    <div
      className="hero-body container"
      style={{
        maxWidth: "725px",
        ...customStyle,
      }}
    >
      {children}
    </div>
  </div>
);

export const PhotoBlock = ({
  photoFileName,
  photoCaption,
  photoPosition,
}: {
  photoFileName: string;
  photoCaption: string;
  photoPosition?: string;
}) => (
  <>
    <div className="static-image">
      <LazyLoadImage
        alt=""
        height="100vh"
        src={require(`../assets/images/scrolly-photos/${photoFileName}`)}
        width="100%"
        effect="blur"
        style={{
          objectPosition: photoPosition || "50% 50%",
        }}
      />
    </div>
    <div className="hero">
      <div
        className="hero-body container py-4"
        style={{
          maxWidth: "1000px",
        }}
      >
        <div className="eyebrow has-text-weight-light">{photoCaption}</div>{" "}
      </div>
    </div>
  </>
);

const mapSequenceText = [
  "The 430 ICE street arrests identified by The City Reporter were clustered in Latino communities across the region, from Passaic and Plainfield in New Jersey to Brentwood and Hempstead on Long Island.",
  "Within New York City, there were 81 ICE street arrests.",
  "Corona had the highest number of ICE street arrests of any New York City neighborhood.",
].join("{newParagraph}");

export const ArticleBody = () => {
  return (
    <>
      <TextBlock customStyle={{ paddingTop: "1.5rem" }}>
        <TopAttribution />
        <p className="copy">
          Federal agents have arrested hundreds of immigrants off New York and
          New Jersey streets in recent months in a stealth enforcement campaign
          that disproportionately targeted people from Latin American countries,
          according to an investigation by The City Reporter based on a review
          of more than 1,200 lawsuits.
        </p>

        <p className="copy">
          More than 93% of the people grabbed off area streets who filed suit
          were from Latin American countries, although Latinos make up only 66%
          of immigrants without legal status in the region.
        </p>

        <p className="copy">
          The arrests have rattled Latino neighborhoods, as people disappear in
          moments as mundane as buying milk, walking their dog, taking out the
          trash or picking up their children from soccer practice.
        </p>

        <p className="copy">
          Street arrests are different from other types of immigration
          enforcement in that they unfold in minutes, often on quiet residential
          streets and out of public view. Many immigrants who had no expectation
          of being detained were targeted at the sole discretion of agents in
          the field. In some accounts of the arrests, Immigration and Customs
          Enforcement (ICE) agents said they stopped people because they looked
          similar to someone they had a warrant for, then realized they had a
          different subject — but apprehended the person anyway.
        </p>

        <p className="copy">
          The surprise encounters often left immigrants stunned, as they feared
          they were being kidnapped. Some ran in terror from the masked agents.
          Other encounters turned violent as officers deployed taser guns and
          smashed car windows. Agents at times shouted racial epithets, calling
          one immigrant a <span className="is-italic">“maldito Mexicano”</span>{" "}
          — “fucking Mexican” — during the arrest,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137799-1-26-cv-00593-cr-petition/#document/p13/a2817827">
            according to court filings
          </OutboundLink>
          .
        </p>
      </TextBlock>

      <MapSequence slideContent={mapSequenceText} />

      <TextBlock>
        <p className="copy">
          Street arrests were rare in the New York metro area before President
          Donald Trump’s second term. As they ramped up in recent months,
          lawyers have sued the administration, arguing the arrests violate the
          Constitution, and federal judges have increasingly slammed ICE’s
          tactics as illegal.
        </p>

        <p className="copy">
          The City Reporter’s findings come as a federal judge has{" "}
          <OutboundLink to="https://www.thecityreporter.nyc/2026/05/19/ice-arrest-26-federal-plaza-judge-order/">
            barred
          </OutboundLink>{" "}
          most ICE arrests at immigration courthouses in New York City and as
          White House border czar Tom Homan has threatened to “
          <OutboundLink to="https://x.com/joemoeller44/status/2051696560673804577">
            flood the zone
          </OutboundLink>
          ” with ICE agents, raising the specter that racially-targeted street
          arrests could escalate sharply.
        </p>

        <p className="copy">
          Since the vast majority of these arrests have gone unreported and
          federal immigration data does not distinguish street arrests from
          others, The City Reporter{" "}
          <OutboundLink to="https://www.thecityreporter.nyc/2026/05/27/street-arrests-immigration-methodology-habeas-petitions/">
            reviewed every emergency lawsuit
          </OutboundLink>{" "}
          filed by immigrants challenging the legality of their detention in
          three federal courts from October 15, 2025 to March 15, 2026. Known as
          habeas corpus petitions, the lawsuits have{" "}
          <OutboundLink to="https://habeasdockets.org/">
            skyrocketed
          </OutboundLink>{" "}
          in response to the Trump administration’s enforcement campaign. Many
          of them offer rich details about the arrests, including the locations,
          circumstances and demographics of people arrested.
        </p>

        <p className="copy">
          In the database of petitions, The City Reporter identified 430 street
          arrests across the New York metro area, including Long Island and New
          Jersey. Difficult to track, these arrests have drawn little public
          scrutiny compared to the ICE arrests inside immigration court at{" "}
          <OutboundLink to="https://www.thecityreporter.nyc/2025/08/11/26-federal-plaza-immigration-court-trump-arrests-data-analysis/">
            26 Federal Plaza
          </OutboundLink>
          , which have attracted throngs of court observers, elected officials
          and photographers. Yet during the five-month period of our review, The
          City Reporter found that street arrests were far more common than
          immigration court arrests in the habeas petitions.
        </p>

        <BarChart />

        <p className="copy">
          Within The City Reporter’s dataset, Latinos comprised nearly all of
          the street arrests in the region while they accounted for just 55% of
          arrests in administrative settings like ICE check-ins, U.S.
          Citizenship and Immigration Services (USCIS) appointments and
          immigration court hearings.
        </p>

        <WaffleChart />

        <p className="copy">
          “The data confirms what all New Yorkers know, which is that ICE
          engages in racial profiling when carrying out street arrests,” said
          Elora Mukherjee, a law professor at Columbia University and the
          director of their Immigrants’ Rights Clinic, in response to The City
          Reporter’s findings.
        </p>

        <p className="copy">
          In a statement, the Department of Homeland Security said: “Allegations
          that DHS law enforcement engages in ‘racial profiling’ are disgusting,
          reckless, and categorically FALSE. What makes someone a target for
          immigration enforcement is if they are illegally in the U.S.—NOT their
          skin color, race, or ethnicity.”
        </p>

        <ColumnChart />

        <p className="copy">
          Not every immigrant who is arrested files a habeas petition; only
          about one in ten did so during these five months. The actual number of
          street arrests is likely much higher than what is captured in The City
          Reporter’s database.
        </p>

        <p className="copy">
          As word has spread through some Latino neighborhoods — where ICE
          agents show up again and again — many prepare for the worst. Sisters
          plead with brothers to share their locations with them, day laborers
          avoid work, and men write the numbers of emergency contacts in Sharpie
          marker on their arms.
        </p>

        <p className="copy">
          On an afternoon last December, ICE agents surrounded Juan and arrested
          him while he was watching a game of dominos on Staten Island. At the
          time, he had lived in the United States for nearly two decades and had
          just visited with his two children, who are both U.S. citizens.
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="041526_staten_island_ice-4.jpg"
        photoCaption="Juan was surrounded by ICE agents on Staten Island while he was watching a game of dominoes last December, April 15, 2026. Ben Fractenberg/The City Reporter"
        photoPosition="60%"
      />

      <TextBlock>
        <p className="copy">
          “You never know. You could just be heading to the store and get
          arrested right around the corner,” Juan, who was released after filing
          a habeas petition, told The City Reporter in Spanish. Like all
          immigrants quoted in this article, he is not identified by his full
          name out of fear of retaliation in their ongoing immigration
          proceedings.
        </p>

        <p className="copy">
          “The truth is, I walk around in fear,” Juan said.
        </p>
      </TextBlock>

      <SectionAnimation section="Section 1 Subhed" />

      <TextBlock>
        <p className="copy">
          It was already dark as Florencio was coming home to Corona, Queens
          after a day of painting an apartment in Manhattan. When he reached his
          block, a masked man shouted at him. As more men emerged from their
          cars, he feared he was about to be kidnapped.
        </p>

        <p className="copy">
          At the same moment, his wife, Fidelina, was corralling their three
          children out the door of their home for a grocery store run before
          dinner. Their 13-year old son heard the shouts and tugged at his
          mother. “Mama, it’s ICE,” he said.
        </p>

        <p className="copy">
          Under the dull glow of street lamps, Fidelina wasn’t sure. Her
          nine-year-old son recognized his father first. “It’s daddy!” he
          shouted.
        </p>

        <p className="copy">
          Florencio stole a glance towards his children just a few feet away,
          then turned and ran. Outnumbered, he felt a pair of hands press into
          his back. The force sent him to the ground so fast that his ankle
          twisted and his forehead skidded across the asphalt.
        </p>

        <p className="copy">
          ICE agents handcuffed him, shoved him into an unmarked car and whisked
          him away. The account of Florencio’s arrest is based on court records,
          ICE records and extensive interviews with the family.
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="032726_habeas_corona_family.jpg"
        photoCaption="Florencio with his young daughter in Corona, Queens, March 27, 2026. Ben Fractenberg/The City Reporter"
        photoPosition="50% 0%"
      />

      <TextBlock>
        <p className="copy">
          Florencio’s detention is part of a pattern across the region where ICE
          agents stopped and arrested people in Latino neighborhoods based on
          their appearance, while they claimed to be looking for other people,
          according to internal ICE records. In court filings and interviews,
          lawyers said that the arrests violate constitutional protections from
          unreasonable search and seizure and that agents often don’t have
          enough evidence to make the arrests at all.
        </p>

        <p className="copy">
          “As soon as someone is not free to leave, that's a seizure, and
          there’s no basis for the seizure in most of these cases,” said Paige
          Austin, an attorney for Make the Road, an immigrant advocacy
          organization that has handled dozens of habeas petitions in the last
          year. Instead, they’re making assumptions based on appearance and what
          jobs they work and where they live, she said. “It's just pure racial
          profiling.”
        </p>

        <p className="copy">
          In ICE’s account of his arrest, agents said they’d first approached
          Florencio because he had “the same build and likeness” to a person
          they were trying to arrest that day. The agency claimed that Florencio
          voluntarily admitted he had crossed the border illegally before they
          tried to arrest him.
        </p>

        <p className="copy">
          But in an interview with The City Reporter and in his court filing,
          Florencio disputed that account, saying he was already handcuffed when
          agents drove him to a nearby parking lot where they rifled through his
          wallet, took his phone and snapped a photo of his face. They showed
          him pictures of the man they said they were looking for and told him
          the man was from Ecuador.
        </p>

        <p className="copy">
          “I’m not Ecuadorian. I’m Guatemalan. I’ve been here for almost 13
          years,” he recalled pleading with the agents in Spanish.
        </p>

        <p className="copy">
          The agents told him it didn’t matter, Florencio said. “‘You have to
          come with us because you’re here without papers, too.’”
        </p>
      </TextBlock>

      <NewsletterSignupBanner />

      <TextBlock>
        <p className="copy">
          ICE{" "}
          <OutboundLink to="https://www.courtlistener.com/docket/72146903/81/hussen-v-noem/">
            maintains
          </OutboundLink>{" "}
          that its stops are legal and consistent with the constitution. “Our
          agents are properly trained to determine alienage and removability. If
          and when we do encounter individuals subject to arrest, our law
          enforcement is trained to ask a series of well-determined questions,”
          the Department of Homeland Security said in a statement to The City
          Reporter.
        </p>

        <p className="copy">
          But in dozens of cases, immigrants repeatedly described being
          surrounded by multiple cars and swarmed by officers, which they say
          made it impossible for them to leave.
        </p>

        <p className="copy">
          “They’re arresting first, then they’re finding the justification
          later,” said Michael Musa-Obregon, an immigration lawyer based in
          Queens who frequently files habeas petitions. “It’s all illegal.”
        </p>

        <p className="copy">
          In early February, ICE agents were in Port Richmond, Staten Island,
          looking for a 25-year-old man from Mexico named Julio,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28144910-1-26-cv-01295-vera-angel-i213/#document/p2/a2818069">
            according to an ICE document
          </OutboundLink>
          . They circled the same residential block again and again, first
          detaining a 36-year-old from Guatemala named Isaias, then a
          21-year-old named Juan, also from Guatemala.{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137864-1-26-cv-01180-tautiu-i213/#document/p2/a2817809">
            In separate arrest reports
          </OutboundLink>
          , ICE officials{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137794-1-26-cv-01214-chox-i213/#document/p2/a2817810">
            described
          </OutboundLink>{" "}
          each as “a male who was believed to be the intended target.”
        </p>

        <p className="copy">
          Agents then arrested a 47-year-old man from Mexico named Alejandro
          because he was leaving the location they were staking out,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28144910-1-26-cv-01295-vera-angel-i213/#document/p2/a2818071">
            they said
          </OutboundLink>
          .
        </p>

        <p className="copy">
          After being apprehended, Isaias and Juan both decided to leave the
          country, federal court records indicate. A judge{" "}
          <OutboundLink to="https://www.courtlistener.com/docket/72284373/9/vera-angel-v-genalo/">
            later ordered
          </OutboundLink>{" "}
          Alejandro’s release after lawyers filed a habeas petition.
        </p>

        <p className="copy">
          DHS said that it sought the cooperation of the NYPD to locate Julio,
          its original target, but the police agency declined because of
          sanctuary laws.
        </p>

        <p className="copy">
          In another case, a 22-year-old from Guatemala was walking to his job
          as a vegetable grocer in Sunset Park, Brooklyn when six officers
          approached him on the street. They pulled out some photos of men for
          him to identify. “You’re Latino, you look like one of them. Do you
          know any of these guys?” the agent asked,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137792-1-26-cv-00597-chom-petition/#document/p4/a2817814">
            according to court documents.
          </OutboundLink>
        </p>

        <p className="copy">
          When he said no, the officers asked him for identification and asked
          if he had papers. The 22-year-old gazed at their firearms and felt he
          had no choice but to respond, he{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137792-1-26-cv-00597-chom-petition/#document/p12/a2818085">
            said in filings
          </OutboundLink>
          . Then, they arrested him, too. In{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137795-1-26-cv-00597-chom-i-213/#document/p2/a2817815">
            ICE’s account of the arrest
          </OutboundLink>
          , agents were driving to arrest someone else when they “noticed a
          person matching the target’s biographic information wearing a hooded
          jacket.
        </p>

        <p className="copy">
          Immigrant advocates across the country have been challenging these
          arrest tactics in court. The American Civil Liberties Union has sued
          in{" "}
          <OutboundLink to="https://www.aclusocal.org/cases/vasquez-perdomo-v-noem/">
            California
          </OutboundLink>{" "}
          and{" "}
          <OutboundLink to="https://www.aclu.org/press-releases/aclu-sues-federal-government-to-end-ice-cbps-practice-of-suspicionless-stops-warrantless-arrests-and-racial-profiling-of-minnesotans">
            Minnesota
          </OutboundLink>{" "}
          alleging ICE was using racial profiling to make warrantless arrests.
          In April, the New York Civil Liberties Union and other immigrant
          advocacy groups brought a similar{" "}
          <OutboundLink to="https://www.thecityreporter.nyc/2026/04/09/ice-lawsuit-new-york-racial-profiling/">
            class action lawsuit
          </OutboundLink>{" "}
          arguing these arrests violate the constitutional rights of immigrants.
        </p>

        <p className="copy">
          “It's an outrageous racial profiling policy of stopping Latino men
          primarily,” said Amy Belsher, the NYCLU’s director of immigrants’
          rights litigation. “It’s enormously harmful.”
        </p>

        <p className="copy">
          Last September, Supreme Court Justice Brett Kavanaugh wrote in{" "}
          <OutboundLink to="https://www.supremecourt.gov/opinions/24pdf/25a169_5h25.pdf">
            an opinion
          </OutboundLink>{" "}
          that race could be one factor when making arrests following ACLU’s
          California suit. The ruling allowed ICE agents to continue using race
          or ethnicity as one consideration in its immigration enforcement
          arrests around Los Angeles.
        </p>

        <p className="copy">
          While the Kavanaugh opinion applied only to that area, it set a tone
          for ICE enforcement tactics across the country, said Mukherjee, the
          Columbia Law professor.
        </p>

        <p className="copy">
          “The effects of that decision have reverberated across the country,
          including here in New York, where ICE officers and other federal
          enforcement officers working with ICE are emboldened to carry out
          racial profiling,” she said.
        </p>

        <p className="copy">
          As petitions describing street arrests increasingly jam up court
          dockets, some federal judges in New York have condemned ICE’s
          practices.
        </p>

        <p className="copy">
          In January, a 24-year-old from Honduras{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28145562-2-26-cv-00029-garcia-lanzapdf/#document/p2/a2818103">
            was walking to catch the LIRR in Hempstead
          </OutboundLink>{" "}
          when seven cars full of immigration enforcement agents surrounded him.
          The officers arrested him without asking any questions and then later
          produced a warrant. Officials{" "}
          <OutboundLink to="https://www.courtlistener.com/docket/72102483/15/garcia-lanza-v-noem/">
            admitted
          </OutboundLink>{" "}
          that they arrested the wrong person, wrote Gary Brown, a{" "}
          <OutboundLink to="https://www.nyed.uscourts.gov/district-judge-gary-r-brown">
            Trump-appointed
          </OutboundLink>{" "}
          federal judge overseeing the case. ICE agents, he concluded, “grossly
          abused their authority.”
        </p>

        <p className="copy">
          “The abhorrent and illegal practices identified in this opinion are
          not limited to this case but are seemingly being inflicted on a
          widespread basis,” Brown continued. “This isn’t how things are
          supposed to work in America.”
        </p>
      </TextBlock>

      <SectionAnimation section="Section 2 Subhed" />

      <TextBlock>
        <p className="copy">
          Snow was still piled up on the street this February when Diego, from
          Ecuador, was surrounded by three ICE agents as he sat in his vehicle.
        </p>

        <p className="copy">
          <span className="is-italic">“Auxilio! Ayúdenme!”</span> he shouted.
          The pleas for help drew a neighbor to her window, who witnessed the
          arrest and{" "}
          <OutboundLink to="https://www.facebook.com/reel/4445305782461464">
            partly captured
          </OutboundLink>{" "}
          what happened on her phone.
        </p>

        <p className="copy">
          When the neighbor finally saw Diego emerge from the vehicle, his face
          was bloody, she said. Pebbles of glass from his shattered driver-side
          window sprayed the street a day after his arrest. His injuries were
          later treated at a local hospital,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137801-1-26-cv-01360-caceido-petition/#document/p2/a2817816">
            court records show
          </OutboundLink>
          .
        </p>

        <p className="copy">
          “I felt so much sadness. No one wanted to go outside,” the neighbor
          told The City Reporter.
        </p>

        <p className="copy">
          Diego was taken just around the corner from where Florencio had been
          tackled in front of his wife and children a few months earlier.
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="021726_ice_corona-2.jpg"
        photoCaption="A day after Diego’s arrest in Corona, glass from his car window lay strewn across the pavement. Feb. 17, 2026. Credit: Gwynne Hogan/The City Reporter"
        photoPosition="90%"
      />

      <TextBlock>
        <p className="copy">
          Diego’s arrest was one of 29 incidents The City Reporter identified
          across the region in which federal agents are accused in court records
          of using force while making a street arrest. According to ICE{" "}
          <OutboundLink to="https://www.ice.gov/doclib/policy/19009.3.pdf">
            policy
          </OutboundLink>
          , agents may use force only as a last resort. Excessive use of force
          is prohibited. But agents, who are often masked, have bloodied men,
          drawn firearms, zapped people with stun guns, smashed car windows and
          forcibly dragged people out of their vehicles, according to accounts
          in the habeas petitions.
        </p>

        <p className="copy">
          “There's absolutely no need for the force except to terrorize people,”
          said Stephanie Cordero, a lawyer with Latino Justice who has
          represented immigrants in habeas proceedings. One of her clients had a
          gun held to his head and was hit by an ICE agent’s vehicle as he was
          arrested outside of a Home Depot on Long Island,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28145860-1-26-cv-01170-gonzalez-guardado/#document/p2/a2818101">
            the petition alleges
          </OutboundLink>
          .
        </p>

        <p className="copy">
          All of the claims of violence reviewed by The City Reporter involved a
          Latino person.
        </p>

        <p className="copy">
          In Plainfield, New Jersey, a woman from El Salvador was loading
          laundry into her car when agents{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137800-1-26-cv-02439-abrego-petition/#document/p15/a2817821">
            allegedly
          </OutboundLink>{" "}
          pulled her to the ground so roughly that she was{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137800-1-26-cv-02439-abrego-petition/#document/p16/a2817819">
            left with fractured ribs
          </OutboundLink>
          . A man from the Dominican Republic who lives in Freeport, Long
          Island, was walking to the restaurant where he worked when, he said in{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137791-1-26-cv-01482-vjca-petition/#document/p6/a2817824">
            court documents,
          </OutboundLink>{" "}
          agents pointed a gun at his head and twisted his arm, injuring his
          shoulder. And a man from Guatemala was punched repeatedly and shot
          with a stun gun several times during his arrest in Corona,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137797-1-26-cv-00942-lsm-affidavit/#document/p2/a2817825">
            he said in sworn testimony
          </OutboundLink>
          .
        </p>

        <p className="copy">
          “ICE law enforcement officers are trained to use the minimum amount of
          force necessary to resolve dangerous situations to prioritize the
          safety of the detainees, the public, and our officers,” DHS said in a
          statement, adding that its officers regularly receive use of force
          training.
        </p>

        <p className="copy">
          Immigration lawyers said they do their best to document what they can,
          but clients are often wary of filing additional legal claims. By
          submitting a habeas claim to challenge their detention, they are
          already opening another proceeding in federal court, in addition to
          their immigration case.
        </p>

        <p className="copy">
          “There’s a hierarchy of needs when we're talking with our clients,”
          said Cordero, the lawyer from Latino Justice. “The priority is to get
          people released from detention.”
        </p>

        <p className="copy">
          Nationwide, there has been little oversight about the use of force in
          immigration arrests, which have surged to{" "}
          <OutboundLink to="https://ice-arrests.apps.deportationdata.org/">
            nearly 400,000
          </OutboundLink>{" "}
          since Trump took office last year. While federal law enforcement
          officers have broad protections, prosecutors recently filed{" "}
          <OutboundLink to="https://www.nytimes.com/2026/05/18/us/ice-agent-charges-venezuelan-immigrant.html">
            state
          </OutboundLink>{" "}
          felony{" "}
          <OutboundLink to="https://www.fox9.com/news/ice-agent-appears-court-assault-charges-from-highway-gun-pointing-incident">
            charge
          </OutboundLink>
          s against two immigration agents in Minnesota.
        </p>

        <p className="copy">
          At least five states including New Jersey have{" "}
          <OutboundLink to="https://statedemocracy.law.wisc.edu/our-work/state-created-damages-remedies-against-federal-officials">
            passed legislation
          </OutboundLink>{" "}
          that entitles individuals to monetary damages if federal agents
          violate their constitutional rights. In New York, a{" "}
          <OutboundLink to="https://www.nysenate.gov/legislation/bills/2025/S8500/amendment/A">
            similar
          </OutboundLink>{" "}
          law was included as part of the state budget this month. It will allow
          people to sue retroactively over alleged offenses, going back to
          January 2025.
        </p>

        <p className="copy">
          In New York City, one ICE supervisor, Brenden Cuni, was named in
          multiple lawsuits where immigrants said they were beaten up and shot
          with stun guns. He is the only agent to be named in the habeas
          petitions reviewed by The City Reporter.
        </p>

        <p className="copy">When reached by phone, Cuni declined to comment.</p>

        <p className="copy">
          One of those arrests involved a 21-year-old in The Bronx back in
          October. After providing his identification, agents told him he was
          being arrested. An agent tripped him, he said, and he fell face first
          to the pavement, breaking a tooth.
        </p>

        <p className="copy">
          As this petitioner was on the ground, Cuni began using a Taser on his
          legs, the lawsuit alleges. He begged the agents to stop, but Cuni only
          told him to shut up,{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137799-1-26-cv-00593-cr-petition/#document/p12/a2817828">
            according to court records
          </OutboundLink>
          . Cuni then discharged his stun gun a half dozen times in total.
        </p>

        <p className="copy">
          “Tasing feels really ugly. Your heart beats really fast and your whole
          body moves, even if you don't want it to,” the 21-year old{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137796-1-26-cv-00593-cr-affidavit/#document/p3/a2817830">
            said in an affidavit
          </OutboundLink>{" "}
          submitted in court.
        </p>

        <p className="copy">
          Unable to stand, officers carried his crumpled and bleeding body into
          the van. Later he overheard the officers laughing as Cuni{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137799-1-26-cv-00593-cr-petition/#document/p13/a2817827">
            called him
          </OutboundLink>{" "}
          a <span className="is-italic">“maldito Mexicano”</span> or “fucking
          Mexican,” he said. “This is when I realized that the man had arrested
          me just for being Hispanic.”
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="102126_ice_canal_raid_cuni-2.jpg"
        photoCaption="ICE supervisor Brenden Cuni was among those agents who participated in a raid targeting West African street vendors along Canal Street, Oct. 21, 2025. Alex Krales/The City Reporter"
      />

      <TextBlock>
        <p className="copy">
          <OutboundLink to="https://www.documentcloud.org/documents/28138140-1-26-cv-00593-cr-medical-record/#document/p1/a2817831">
            Medical records confirmed
          </OutboundLink>{" "}
          the use of a Taser by the ICE officers. In{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137793-1-26-cv-00593-cr-i213/#document/p2/a2817832">
            their account of the arrest
          </OutboundLink>
          , ICE said the man resisted arrest and was warned multiple times
          before the agent discharged his stun gun. DHS told The City Reporter
          he had become “assaultive and combative."
        </p>

        <p className="copy">
          A month later, Cuni was named as the officer{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137804-1-26-cv-009420-lsm-petition/#document/p5/a2817833">
            in a second habeas lawsuit
          </OutboundLink>{" "}
          for a man who was stunned with a Taser repeatedly in Corona. DHS said
          the man injured officers as they detained him and deployed the weapon
          in self-defense and to protect the public.
        </p>

        <p className="copy">
          “They feel so empowered to just do whatever they want,” said John
          Leschak, an immigration attorney in New Jersey. He has filed four
          habeas lawsuits on behalf of clients who have been arrested after ICE
          agents smashed their car windows.
        </p>

        <p className="copy">
          Julia, one of Leschak’s clients, was driving to a pharmacy to pick up
          medication in New Jersey with her husband when she noticed immigration
          officers following them. They drove home instead. After pulling into
          their driveway, agents ordered them out of the car. When they stayed
          put, one smashed a window. Officers pulled Julia from the vehicle and
          pressed her husband against the car, while rifling through his
          pockets. She begged them not to hurt him, explaining he had a lung
          problem and that they weren’t criminals. “I’m going to send you back
          to your country: Mexico,” one taunted her, according to her suit.
        </p>

        <p className="copy">
          Julia had trouble breathing, she was so scared. Later, while shackled
          and handcuffed inside the unmarked car, she asked why they had
          targeted her. One of the officers responded that “anyone who happens
          to cross our path right now is fair game. We're out here to pick
          people up,” she recalled in an interview.
        </p>

        <p className="copy">
          Julia was eventually released, but her husband was deported. The sound
          of sirens still makes her skin prickle with fear. “It’s an act of
          abuse. They treat you like animals,” she told The City Reporter in
          Spanish.
        </p>
      </TextBlock>

      <SectionAnimation section="Section 3 Subhed" />
      <TextBlock>
        <p className="copy">
          Early last November, Rosi answered a call at around noon from an
          unfamiliar number. On the phone, her boyfriend Jose explained that he
          had been arrested by ICE agents that morning as he was headed to work.
          Agents had grabbed one of their neighbors, too, he said.
        </p>

        <p className="copy">
          Rosi spent a few moments in quiet shock before she started frantically
          searching online for lawyers, she later recalled. Many replied that
          there was nothing they could do for her while others asked for large
          amounts of cash up front.
        </p>

        <p className="copy">
          "They grab them in the moment you least expect, she told The City
          Reporter in Spanish. “There were no instructions telling you exactly
          what to do, so you just try to do whatever is within your power.”
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="031426_corona_immigration_couple-3.jpg"
        photoCaption="Rosi recounted a harrowing 10 days when her partner, Jose, spent in ICE detention before a federal judge ordered his release, March 14, 2026. Ben Fractenberg/The City Reporter"
        photoPosition="50% 0%"
      />

      <TextBlock>
        <p className="copy">
          After two days, Rosi was connected to a pro-bono lawyer, who filed a
          petition challenging Jose’s arrest as “part of an operation of
          indiscriminate stops of Hispanic people in the Corona, Queens
          neighborhood.” A judge ordered his release 10 days later.
        </p>

        <p className="copy">
          As street arrests sweep up people like Jose, their loved ones are left
          to navigate an inscrutable legal system that the Trump administration
          is reshaping at breakneck speed. Even with proper representation,
          immigrants face a shifting legal landscape in which federal courts are
          interpreting the administration’s policies in contradictory ways.
        </p>

        <p className="copy">
          Federal courts in New York and California have been especially
          favorable to people who file habeas petitions, often ordering people
          released right away. But in Louisiana and Texas, home to some of ICE’s
          largest facilities, federal judges{" "}
          <OutboundLink to="https://www.courthousenews.com/fifth-circuit-upholds-trump-administrations-mandatory-detention-policy/">
            have upheld
          </OutboundLink>{" "}
          the Trump administration’s policy of mandatory detention that began
          last July.
        </p>

        <p className="copy">
          “Outside of those districts, to get somebody out and restore them to
          where they were before ICE illegally arrested them is really
          difficult,” said Reuben Kerben, who runs an immigration law practice
          in Queens and represents clients across the country.
        </p>

        <p className="copy">
          Since detainees are frequently moved across state lines, and since
          habeas petitions must be filed in the same jurisdiction in which the
          person is being detained, area families are on the clock to file a
          lawsuit quickly, while the arrested immigrant is still in New York.
        </p>

        <p className="copy">
          Although Fidelina didn’t quite know it at the time, that race to file
          began the minute ICE grabbed her husband Florencio last November. Like
          Rosi, she began calling immigration attorneys looking for legal
          assistance, but most didn’t know how to help her.
        </p>

        <p className="copy">
          As Fidelina kept searching for a lawyer, she saw an ad for what she
          believed were nonprofit legal services through Catholic Charities and
          started messaging with a man over WhatsApp. He promised to represent
          her for free.
        </p>

        <p className="copy">
          All Fidelina would have to pay, he told her, were the administrative
          costs of filing the documents in the case. She sent more than a dozen
          payments using the app, Zelle, and received detailed receipts with
          official-looking government seals.
        </p>

        <p className="copy">
          Then, she attended what she believed was an immigration court hearing
          through a WebEx video conference link. Two weeks and $13,000 later,
          payment records show, she realized it was all a scam.
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="040226_fidelina_habeas-2.jpg"
        photoCaption="Fidelina, right, and her cousin Ana witnessed ICE take Florencio near their home in Queens. In her desperation to find a lawyer, she was scammed out of thousands of dollars, April 2, 2026. Alex Krales/The City Reporter"
        photoPosition="60% 0%"
      />

      <TextBlock>
        <p className="copy">
          By the time Fidelina got in touch with a legitimate private attorney{" "}
          who filed a habeas petition in federal court, two months had passed.
          In that time, Florencio had been transferred from New York to New
          Jersey to Texas and then finally to New Mexico. When his children
          called on Christmas and asked if he was coming home to celebrate with
          them, he took a deep breath to stop himself from crying.
        </p>

        <p className="copy">
          It was February by the time a federal judge in New Mexico ordered that
          immigration court grant him a bond hearing. Florencio was granted
          release. If he had been held in Texas where some federal judges have{" "}
          <OutboundLink to="https://www.courthousenews.com/fifth-circuit-split-on-trumps-mandatory-detention-policy/#:~:text=The%20appeals%20court%20previously%20allowed,this%20violates%20due%20process%20rights.">
            sided
          </OutboundLink>{" "}
          with the administration's policy of mandatory detention without bond,
          he might still be locked up. The outcome of habeas petitions still
          vary by judges and jurisdictions.
        </p>

        <p className="copy">
          Four months after he was taken, he was dropped off at an airport near
          the detention center, left to find his own way home.
        </p>

        <p className="copy">
          “I wish someone had told me, ‘Go to this lawyer, he’s good. Do these
          things so they don’t move your husband. But for me it wasn’t like
          that,” said Fidelina. “I had to learn the hard way.”
        </p>
      </TextBlock>

      <SectionAnimation section="Section 4 Subhed" />
      <TextBlock>
        <p className="copy">
          On a wintry Friday night in Port Richmond, Staten Island, advocates
          from Make the Road hosted a meeting to address the recent ICE arrests
          in the predominantly Latino community. The turnout was smaller than
          expected, as many immigrants remained worried about agents lurking in
          unmarked cars. An organizer passed around a stack of wallet-sized red
          cards with the Fourth and Fifth Amendments and reminded attendees that
          if they’re stopped, agents still need a reason to detain them.
        </p>

        <p className="copy">
          “Probable cause technically can’t be based on someone’s race or how
          someone speaks, but we know they use it for racial profiling,” said
          the lead organizer, Luba Cortés, speaking in Spanish, who urged
          participants not to volunteer information about themselves if
          confronted by immigration agents.
        </p>

        <p className="copy">
          The meeting is just one of many efforts unfolding across New York in
          response to the Trump administration’s arrest tactics. After ICE made
          spectacular and concentrated shows of force in Los Angeles and Chicago
          last year, New York seemed poised for a similar showdown when{" "}
          <OutboundLink to="https://www.thecityreporter.nyc/2025/10/21/ice-raids-canal-street-trump-immigration/">
            agents in tactical gear
          </OutboundLink>{" "}
          flooded Canal Street targeting street vendors. Within minutes, hordes
          of angry New Yorkers formed a spontaneous protest, swarming and
          heckling the agents. A month later, activists got word of another
          potential Canal Street raid. After protests broke out, ICE agents
          eventually{" "}
          <OutboundLink to="https://www.thecityreporter.nyc/2025/11/29/nypd-ice-homeland-security-canal/">
            left the area
          </OutboundLink>{" "}
          without making any arrests.
        </p>

        <p className="copy">
          The Department of Homeland Security at the time called the protesters
          “violent agitators” and said they would “never apologize for enforcing
          the law and removing criminal illegal aliens.” In the months since,
          activists have built out a citywide{" "}
          <OutboundLink to="https://www.instagram.com/p/DRQMcYhDqDm/">
            hotline
          </OutboundLink>{" "}
          to track ICE sightings and notify volunteers, who rush to the scene of
          a potential arrest.
        </p>

        <p className="copy">
          That’s what happened one afternoon in February when agents were
          spotted outside an apartment building in Bushwick, Brooklyn. Within
          minutes, volunteers arrived and confirmed that a man from Mexico had
          been grabbed while returning from work. Nearby, dozens of people{" "}
          <OutboundLink to="https://www.amny.com/news/ice-raid-brooklyn-pepper-sprayed-02242026/">
            surrounded
          </OutboundLink>{" "}
          the ICE agents’ car, blowing whistles, pounding their windows and
          throwing construction barriers in their path.
        </p>

        <p className="copy">
          The volunteers were able to connect his family to lawyers, who helped{" "}
          rapidly{" "}
          <OutboundLink to="https://www.documentcloud.org/documents/28137803-1-26-cv-01556-casimiro-petition/">
            file a{" "}
          </OutboundLink>
          <OutboundLink to="https://www.documentcloud.org/documents/28137803-1-26-cv-01556-casimiro-petition/">
            habeas petition
          </OutboundLink>
          . A{" "}
          <OutboundLink to="https://storage.courtlistener.com/recap/gov.uscourts.nysd.658539/gov.uscourts.nysd.658539.7.0.pdf">
            judge ordered
          </OutboundLink>{" "}
          his release in just over a week.
        </p>

        <p className="copy">
          But on many occasions, street arrests have eluded activists, who show
          up after ICE vehicles have sped away. The uptick of street arrests has
          put many Latino neighborhoods across the city on edge.
        </p>

        <p className="copy">
          In Sunset Park, arrests have been concentrated along a short stretch
          of the neighborhood. Some residents started writing the phone numbers
          of family members on their arms in case they’re stopped. A school
          offered escorts for parents and students walking home and a nearby
          church saw a drop in Sunday mass participants.
        </p>

        <p className="copy">
          “It's nerve wracking,” said Samuel Cruz, a pastor at Trinity Lutheran
          Church. “Anyone who’s Latino has to be concerned with these ICE
          officers.”
        </p>

        <p className="copy">
          Alexa Avilés, the local City Council member, said that her office at
          times received multiple calls reporting street arrests in the area a
          day and works to connect families with resources when possible.
        </p>

        <p className="copy">
          “The tactics that we’re seeing are they’re coming faster and taking
          people faster,” she said. “There’s a good number of people they’re
          taking that we don't know who they are or what happened to them.”
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="032026_aviles_trinity_church.jpg"
        photoCaption="Council member Alexa Avilés said her office at times has received multiple calls a day for people arrested within her Sunset Park district. Alex Krales/The City Reporter"
        photoPosition="85% 90%"
      />

      <TextBlock>
        <p className="copy">
          Even for those who are able to get out of ICE detention through a
          habeas petition, it’s only a temporary reprieve. After their release,
          they remain in deportation proceedings and must navigate the byzantine
          immigration court system.
        </p>

        <p className="copy">
          For days after Jose got out, he and his girlfriend Rosi hunkered down
          in their apartment, too scared to leave. Once routine errands like
          going to the laundromat or the grocery store suddenly became potential
          sources of terror. While their own lives had been rocked, the world
          around them seemed unaffected.
        </p>

        <p className="copy">
          “People seem so normal, walking around without fear,” said Rosi in
          Spanish.
        </p>

        <p className="copy">
          Juan, who was arrested on Staten Island, had lived in the United
          States for nearly two decades before his arrest last December. Since
          his release, he uses an app on his phone to check in with ICE each day
          about his location. Sometimes officers come by to make sure he’s at
          home.
        </p>
      </TextBlock>

      <PhotoBlock
        photoFileName="032726_habeas_corona_family-4.jpg"
        photoCaption="Since his return, Florencio’s children are often anxious when he leaves the house. Ben Fractenberg/The City Reporter"
        photoPosition="55%"
      />

      <TextBlock customStyle={{ marginBottom: "3rem" }}>
        <p className="copy">
          Florencio, now back in Corona, remains anxious that he’ll be taken
          again. His 3-year-old daughter often begs him not to go to work. His
          wife, Fidelina, tries to push away her memories of that day. But
          sometimes it strikes her suddenly, and in an instant she’s back on
          their stoop standing next to her children watching her husband get
          dragged away.
        </p>

        <p className="copy">
          “They have all the power. They have all the authority,” she told The
          City Reporter. “It doesn’t matter to them how many families they
          destroy.”
        </p>
      </TextBlock>
    </>
  );
};
